import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, PenLine, RotateCcw, Sparkles, Target } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, getToken } from "@/api";
import UserAvatar from "@/components/UserAvatar";
import PageBlobs from "@/components/PageBlobs";
import Paywall from "@/components/Paywall";

/* One launcher for the two data-driven practice modes.
   smart     -> POST /smart-practice (AI, consumes 1 generation, weak-area targeting)
   reattempt -> POST /reattempt (stored mistakes only, no generation consumed) */
const MODES = {
  smart: {
    icon: Target,
    pageTestId: "smart-practice-page",
    startTestId: "start-smart-practice-btn",
    title: "Smart Practice",
    tagline: "5 questions selected from areas that need more attention.",
    endpoint: "/smart-practice",
    emptyTitle: "Practice a little first",
    emptyBody:
      "Smart Practice learns from your completed sessions. Finish one custom practice and it will start targeting your weak areas automatically.",
    emptyDetail: "NO_PRACTICE_DATA",
  },
  reattempt: {
    icon: RotateCcw,
    pageTestId: "reattempt-page",
    startTestId: "start-reattempt-btn",
    title: "Reattempt",
    tagline: "The exact questions you got wrong — ready to retry.",
    endpoint: "/reattempt",
    emptyTitle: "No mistakes to reattempt",
    emptyBody:
      "Nice — nothing needs fixing right now. Mistakes from future practice sessions will show up here for a focused retry.",
    emptyDetail: "NO_MISTAKES",
  },
};

const PracticeLauncher = ({ mode }) => {
  const navigate = useNavigate();
  const cfg = MODES[mode] || MODES.smart;
  const Icon = cfg.icon;
  const [me, setMe] = useState(null);
  const [preview, setPreview] = useState(null); // {subject, chapter, difficulty?, count}
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate("/login", { replace: true });
      return;
    }
    apiFetch("/me")
      .then(setMe)
      .catch(() => {});
    apiFetch("/performance/summary")
      .then((intel) => {
        if (mode === "smart") {
          const w = intel.weakAreas?.[0];
          if (w) {
            setPreview({
              subject: w.subject,
              chapter: w.chapter,
              accuracy: w.accuracy,
              wrong: w.wrong,
              attempts: w.attempts,
            });
          } else if (intel.totals?.questions > 0) {
            const chs = (intel.chapters || []).filter((c) => c.attempts > 0);
            chs.sort((a, b) => (a.accuracy ?? 1) - (b.accuracy ?? 1));
            if (chs[0]) setPreview({ subject: chs[0].subject, chapter: chs[0].chapter });
          }
          if (!intel.totals?.questions) setEmpty(true);
        } else {
          if (intel.mistakeCount > 0) {
            const m = intel.weakAreas?.[0];
            setPreview({
              count: intel.mistakeCount,
              subject: m?.subject,
              chapter: m?.chapter,
            });
          } else {
            setEmpty(true);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const start = async () => {
    setStarting(true);
    try {
      const data = await apiFetch(cfg.endpoint, { method: "POST" });
      const r = data.rationale || {};
      navigate("/practice", {
        state: {
          questions: data.questions,
          subject: r.subject,
          chapter: r.chapter,
          difficulty: r.difficulty,
          klass: me?.klass,
          board: me?.board,
          mode,
          reason: r.reason,
        },
      });
    } catch (err) {
      if (err.message === "FREE_LIMIT_REACHED") {
        setPaywallOpen(true);
      } else if (
        err.message === cfg.emptyDetail ||
        err.message === "NO_PRACTICE_DATA" ||
        err.message === "NO_MISTAKES"
      ) {
        setEmpty(true);
      } else {
        // AI/provider failure — the generation was refunded server-side
        toast.error(
          "Abhyas AI is busy right now — your generation was NOT used. Try again in a minute."
        );
      }
      setStarting(false);
    }
  };

  return (
    <div
      data-testid={cfg.pageTestId}
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <header className="relative mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
        <button
          data-testid="launcher-back-btn"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] transition-colors duration-200 hover:border-[#FACC15]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </button>
        <UserAvatar />
      </header>

      <main className="relative mx-auto max-w-xl px-6 pb-20 pt-10 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#16A34A]/15 bg-[#16A34A]/8 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#14532D]">
          <Icon className="h-3.5 w-3.5" />
          {cfg.title}
        </span>
        <h1
          data-testid="launcher-headline"
          className="mt-5 font-display text-3xl font-extrabold leading-tight sm:text-4xl"
        >
          {cfg.tagline}
        </h1>

        {loading ? (
          <div data-testid="launcher-loading" className="mt-12 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-[#16A34A]" />
          </div>
        ) : empty ? (
          <div data-testid="launcher-empty" className="mt-10 rounded-[2rem] border border-[#E3ECE6] bg-white p-10">
            <h2 className="font-display text-xl font-extrabold">{cfg.emptyTitle}</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#647067]">{cfg.emptyBody}</p>
            <Link
              to="/practice-setup"
              data-testid="launcher-custom-practice-link"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
            >
              <PenLine className="h-4 w-4" />
              Start Custom Practice
            </Link>
          </div>
        ) : (
          <div data-testid="launcher-preview" className="mt-10 rounded-[2rem] border border-[#E3ECE6] bg-white p-8 text-left sm:p-10">
            {mode === "smart" && preview ? (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#B45309]">
                  Today's target
                </p>
                <p data-testid="launcher-target" className="mt-2 font-display text-2xl font-extrabold italic">
                  {preview.chapter}
                </p>
                <p className="mt-1.5 text-sm font-semibold text-[#647067]">
                  {preview.subject}
                  {typeof preview.accuracy === "number" && (
                    <> · {Math.round(preview.accuracy * 100)}% correct so far</>
                  )}
                </p>
              </>
            ) : mode === "reattempt" && preview ? (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#B45309]">
                  Waiting for you
                </p>
                <p data-testid="launcher-target" className="mt-2 font-display text-2xl font-extrabold italic">
                  {preview.count} missed question{preview.count === 1 ? "" : "s"}
                </p>
                <p className="mt-1.5 text-sm font-semibold text-[#647067]">
                  {preview.chapter
                    ? `Mostly from ${preview.chapter} (${preview.subject})`
                    : "From your recent practice sessions"}
                </p>
              </>
            ) : null}
            <p className="mt-4 text-xs font-medium leading-relaxed text-[#647067]">
              {mode === "smart"
                ? "Difficulty adapts quietly to how you've been performing — no setup needed."
                : "Uses your saved practice data — this never consumes a generation."}
            </p>
            <button
              data-testid={cfg.startTestId}
              data-magnetic="true"
              onClick={start}
              disabled={starting}
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-sm transition-[background-color,transform,opacity] duration-200 hover:bg-[#15803D] disabled:opacity-50"
            >
              {starting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Preparing your questions...
                </>
              ) : (
                <>
                  Start {cfg.title}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
            {mode === "smart" && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#647067]">
                <Sparkles className="h-3 w-3 text-[#FACC15]" />
                Uses 1 practice generation from your plan
              </p>
            )}
          </div>
        )}
      </main>
      <Paywall open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </div>
  );
};

export default PracticeLauncher;
